import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { GetNodeOptions_getNodeOptions_actionOptions, GetNodeOptions_getNodeOptions_conditionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { IAutomationVariable } from '@frontend/app/types/automations/condition/AutomationVariable';
import { AutomationActionNodeSubType, AutomationNodeType } from '@frontend/app/types/globalTypes';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { Button } from '@/shadcn/components/ui/button';
import { Alert, AlertDescription } from '@/shadcn/components/ui/alert';
import { AutomationNodeV2 } from '../types';
import { ConditionFormV2Enhanced } from './ConditionFormV2Enhanced';
import { SendEmailActionForm, UpdateMemberDataActionForm, AddToOfferActionForm, SendProductCatalogActionForm } from './ActionTypes';
import { InlineTimeSelector } from '../NodeComponents/InlineTimeSelector';
import { getNodeDisplayLabel, NodeIcon } from '../utils/nodeIconMapping';
import { getDelayStateFromDuration } from '../utils/timeUtils';

interface PropertyPanelProps {
  selectedNode: AutomationNodeV2 | null;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  onClose?: () => void;
  actionOptions?: GetNodeOptions_getNodeOptions_actionOptions[];
  conditionOptions?: GetNodeOptions_getNodeOptions_conditionOptions[];
  automationVariables?: IAutomationVariable[];
  onUpdateMessageTemplate?: (updatedTemplate: any) => void;
  resetMessageTemplate?: boolean;
  onRefreshNodeOptions?: () => Promise<any>;
  projectId?: number;
  blueprintName?: string;
  isStaOffer?: boolean;
}

export const PropertyPanel: React.FC<PropertyPanelProps> = ({
  selectedNode,
  onUpdateNode,
  onClose,
  actionOptions = [],
  conditionOptions = [],
  automationVariables = [],
  onUpdateMessageTemplate,
  resetMessageTemplate = false,
  onRefreshNodeOptions,
  projectId,
  blueprintName,
  isStaOffer,
}) => {
  // State for delay node
  const [tempDelayAmount, setTempDelayAmount] = useState(() => {
    if (selectedNode?.type === AutomationNodeType.DELAY && selectedNode?.metadata?.durationMs != null) {
      return getDelayStateFromDuration(selectedNode.metadata!.durationMs).amount;
    }
    return 1;
  });

  const [tempDelayUnit, setTempDelayUnit] = useState(() => {
    if (selectedNode?.type === AutomationNodeType.DELAY && selectedNode?.metadata?.durationMs != null) {
      return getDelayStateFromDuration(selectedNode.metadata!.durationMs).unit;
    }
    return 'hours';
  });

  // Update delay state when selectedNode changes
  useEffect(() => {
    if (selectedNode?.type === AutomationNodeType.DELAY) {
      const duration = selectedNode?.metadata?.durationMs;
      if (duration != null) {
        const { amount, unit } = getDelayStateFromDuration(duration);
        setTempDelayAmount(amount);
        setTempDelayUnit(unit);
      } else {
        // Reset to defaults for unconfigured nodes
        setTempDelayAmount(1);
        setTempDelayUnit('hours');
      }
    }
  }, [selectedNode?.id, selectedNode?.metadata?.durationMs, selectedNode?.type]);

  // Initialize action node metadata on selection to prevent infinite loops
  useEffect(() => {
    if (selectedNode && selectedNode.type === AutomationNodeType.ACTION) {
      const missingFields: { resourceId?: number, fromPreviousSender?: boolean, templateId?: number } = {};
      if (!selectedNode.metadata.resourceId) {
        missingFields.resourceId = 1; // Default resource ID
      }
      if (selectedNode.metadata.fromPreviousSender === undefined) {
        missingFields.fromPreviousSender = false;
      }
      if (selectedNode.subType === AutomationActionNodeSubType.SEND_EMAIL && !selectedNode.metadata.templateId) {
        missingFields.templateId = 1; // Default template ID for email
      }

      // Only update if there are missing fields
      if (Object.keys(missingFields).length > 0) {
        onUpdateNode(selectedNode.id, {
          metadata: {
            ...selectedNode.metadata,
            ...missingFields,
          },
        });
      }
    }
  }, [selectedNode, onUpdateNode]);
  if (!selectedNode) {
    return (
      <Card className="h-full rounded-none border-r-0 border-t-0 border-b-0">
        <CardHeader>
          <CardTitle className="text-base">Properties</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-grey-4">
            <div className="text-sm">Select a node to view its properties</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleMetadataUpdate = (key: string, value: unknown) => {
    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        [key]: value,
      },
    });
  };

  const renderNodeSpecificProperties = () => {
    switch (selectedNode.type) {
      case AutomationNodeType.DELAY: {
        const convertToMs = (amount: number, unit: string): number => {
          const multipliers = {
            hours: 3600000,
            days: 86400000,
          };
          // Round to integer to satisfy API validation (durationMs must be integer)
          return Math.round(amount * (multipliers[unit as keyof typeof multipliers] || 3600000));
        };

        const handleDone = () => {
          handleMetadataUpdate('durationMs', convertToMs(tempDelayAmount, tempDelayUnit));
          onClose?.();
        };

        return (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="text-sm leading-normal text-grey-6">
                Select how long to wait before completing the next step of this automation.
              </div>
              <div>
                <InlineTimeSelector
                  value={tempDelayAmount}
                  unit={tempDelayUnit}
                  onChange={(amount, unit) => {
                    // Only update temporary state, not the actual metadata
                    setTempDelayAmount(amount);
                    setTempDelayUnit(unit);
                  }}
                />
              </div>
            </div>
            <div className="flex justify-start pt-4 border-t border-grey-2 mt-auto flex-shrink-0">
              <Button variant="outline" onClick={handleDone}>
                Done
              </Button>
            </div>
          </div>
        );
      }

      case AutomationNodeType.CONDITION: {
        return (
          <ConditionFormV2Enhanced
            selectedNode={selectedNode}
            onUpdateNode={onUpdateNode}
            onClose={onClose || (() => { })}
            automationVariables={automationVariables}
            conditionOptions={conditionOptions}
            isStaOffer={isStaOffer}
          />
        );
      }

      case AutomationNodeType.ACTION: {
        // Render specific action form based on subType - NO action type selectors shown
        if (selectedNode.subType === AutomationActionNodeSubType.SEND_EMAIL) {
          return (
            <SendEmailActionForm
              selectedNode={selectedNode}
              onUpdateNode={onUpdateNode}
              actionOptions={actionOptions}
              onUpdateMessageTemplate={onUpdateMessageTemplate || (() => { })}
              resetMessageTemplate={resetMessageTemplate}
              onSave={onClose || (() => { })}
              projectId={projectId}
              blueprintName={blueprintName}
            />
          );
        }

        if (selectedNode.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA) {
          return (
            <UpdateMemberDataActionForm
              selectedNode={selectedNode}
              onUpdateNode={onUpdateNode}
              actionOptions={actionOptions}
              conditionOptions={conditionOptions}
              onSave={onClose || (() => { })}
              onRefreshNodeOptions={onRefreshNodeOptions}
            />
          );
        }

        if (selectedNode.subType === AutomationActionNodeSubType.ADD_TO_OFFER) {
          return (
            <AddToOfferActionForm
              selectedNode={selectedNode}
              onUpdateNode={onUpdateNode}
              actionOptions={actionOptions}
              onUpdateMessageTemplate={onUpdateMessageTemplate}
              resetMessageTemplate={resetMessageTemplate}
              onSave={onClose || (() => { })}
            />
          );
        }

        if (selectedNode.subType === AutomationActionNodeSubType.SEND_PRODUCT_CATALOG) {
          return (
            <div>
              {/* Info alert for Send Product Catalog */}
              <Alert variant="info" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  To use this action, ensure the member is added to the Product Catalog stage
                </AlertDescription>
              </Alert>
              <SendProductCatalogActionForm
                selectedNode={selectedNode}
                onUpdateNode={onUpdateNode}
                onSave={onClose || (() => {})}
                projectId={projectId}
                actionOptions={actionOptions}
                onUpdateMessageTemplate={onUpdateMessageTemplate}
                resetMessageTemplate={resetMessageTemplate}
              />
            </div>
          );
        }

        // Fallback for unsupported/unknown subTypes
        return (
          <div className="text-sm text-grey-5">
            Select an action to continue.
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Card className="h-full rounded-none border-0 flex flex-col">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-2 flex-col w-full">
            <div className="flex items-center gap-2">
              <NodeIcon node={selectedNode} size="md" />
              <CardTitle className="text-base m-0">{getNodeDisplayLabel(selectedNode.type, selectedNode.subType as AutomationActionNodeSubType, selectedNode.metadata)}</CardTitle>
            </div>
          </div>
        </div>
      </CardHeader>
      <Separator className="w-full" />

      <CardContent className="flex-1 flex flex-col p-4 relative overflow-hidden">
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {renderNodeSpecificProperties()}
        </div>
      </CardContent>
    </Card>
  );
};
